import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name, email, phone, message });
      toast.success(t('contact.success'));
      setName(''); setEmail(''); setPhone(''); setMessage('');
    } catch (error) {
      toast.error(t('contact.error'));
    } finally {
      setLoading(false);
    }
  };

  const faqs = t('contact.faqs');

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20 bg-white">
      <section className="bg-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{t('contact.overline')}</p>
          <h1 data-testid="contact-title" className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#1A1A1A] mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-serif text-[#1A1A1A] mb-8">{t('contact.infoTitle')}</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#F9F6F0] p-3 rounded-lg"><MapPin size={24} className="text-[#2C5263]" /></div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.address')}</h3>
                    <p className="text-[#666666]">Via del Marinaio<br />30013 Cavallino Treporti (VE)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F9F6F0] p-3 rounded-lg"><Phone size={24} className="text-[#2C5263]" /></div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.phone')}</h3>
                    <a href="tel:+393201122733" data-testid="contact-phone-link" className="text-[#666666] hover:text-[#2C5263] transition-colors">+39 320 112 2733</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F9F6F0] p-3 rounded-lg"><Mail size={24} className="text-[#2C5263]" /></div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.email')}</h3>
                    <a href="mailto:marco@casanereide.it" data-testid="contact-email-link" className="block text-[#666666] hover:text-[#2C5263] transition-colors">marco@casanereide.it</a>
                    <a href="mailto:postmaster@casanereide.it" data-testid="contact-email-secondary-link" className="block text-[#666666] hover:text-[#2C5263] transition-colors text-sm mt-1">postmaster@casanereide.it</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F9F6F0] p-3 rounded-lg"><MessageSquare size={24} className="text-[#2C5263]" /></div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('contact.whatsapp')}</h3>
                    <a href="https://wa.me/393201122733" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="text-[#666666] hover:text-[#2C5263] transition-colors">{t('contact.whatsappText')}</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#F9F6F0] p-8 rounded-lg">
              <h2 className="text-3xl font-serif text-[#1A1A1A] mb-6">{t('contact.formTitle')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{t('contact.nameLabel')} *</label>
                  <input type="text" data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] bg-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{t('contact.email')} *</label>
                  <input type="email" data-testid="contact-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] bg-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{t('contact.phone')}</label>
                  <input type="tel" data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{t('contact.messageLabel')} *</label>
                  <textarea data-testid="contact-message-textarea" value={message} onChange={(e) => setMessage(e.target.value)} rows="5"
                    className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] bg-white resize-none" required />
                </div>
                <button type="submit" data-testid="submit-contact-button" disabled={loading}
                  className="w-full bg-[#2C5263] text-white px-6 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? t('contact.submitting') : t('contact.submitButton')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F9F6F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-[#1A1A1A] mb-12 text-center">{t('contact.faqTitle')}</h2>
          <div className="space-y-6">
            {Array.isArray(faqs) && faqs.map((faq, index) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                data-testid={`faq-item-${index}`}
                className="bg-white p-6 rounded-lg border border-[#2C5263]/10"
              >
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{faq.q}</h3>
                <p className="text-[#666666] leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
